import { useEffect, useState } from "react";
import { Modal, Form, InputNumber, Spin, message } from "antd";
import { matchScoreService } from "@/frontend/services/matchScoreService";
import type { MatchScoreResponseDTO } from "@/shared/dto";

export interface MatchScoreModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  tournamentFormatId: string;
  matchOrder: number;
  matchLabel: string;
  homeTeamName: string;
  awayTeamName: string;
}

export function MatchScoreModal({
  open,
  onClose,
  onSaved,
  tournamentFormatId,
  matchOrder,
  matchLabel,
  homeTeamName,
  awayTeamName,
}: MatchScoreModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<MatchScoreResponseDTO | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    matchScoreService
      .getByEvent(tournamentFormatId)
      .then((res) => {
        if (res.success && res.data) {
          const found = (res.data as MatchScoreResponseDTO[]).find(
            (s) => s.matchOrder === matchOrder,
          ) ?? null;
          setExisting(found);
          form.setFieldsValue({
            homeScore: found?.homeScore ?? 0,
            awayScore: found?.awayScore ?? 0,
          });
        }
      })
      .finally(() => setLoading(false));
  }, [open, tournamentFormatId, matchOrder]);

  function handleClose() {
    form.resetFields();
    setExisting(null);
    onClose();
  }

  async function handleSave() {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (existing) {
        await matchScoreService.update(existing.id, {
          homeScore: values.homeScore,
          awayScore: values.awayScore,
        });
      } else {
        await matchScoreService.create({
          tournamentFormatId,
          matchOrder,
          homeScore: values.homeScore,
          awayScore: values.awayScore,
        });
      }
      message.success("Score guardado");
      onSaved();
      handleClose();
    } catch {
      message.error("Error al guardar el score");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={matchLabel}
      open={open}
      onOk={handleSave}
      onCancel={handleClose}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={saving}
      width={380}
      destroyOnHidden
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" className="pt-2">
          <div className="flex gap-6 items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm font-medium text-gray-600 truncate max-w-[120px] text-center" title={homeTeamName}>
                {homeTeamName}
              </p>
              <Form.Item
                name="homeScore"
                initialValue={0}
                rules={[{ required: true, message: "Requerido" }]}
                className="mb-0"
              >
                <InputNumber min={0} size="large" className="w-24 text-center" controls />
              </Form.Item>
            </div>

            <span className="text-2xl font-bold text-gray-300 mt-6">vs</span>

            <div className="flex flex-col items-center gap-2">
              <p className="text-sm font-medium text-gray-600 truncate max-w-[120px] text-center" title={awayTeamName}>
                {awayTeamName}
              </p>
              <Form.Item
                name="awayScore"
                initialValue={0}
                rules={[{ required: true, message: "Requerido" }]}
                className="mb-0"
              >
                <InputNumber min={0} size="large" className="w-24 text-center" controls />
              </Form.Item>
            </div>
          </div>
        </Form>
      )}
    </Modal>
  );
}
